import cn from 'classnames';
import parse from 'html-react-parser';
import { NextPage } from 'next';
import { useSession } from 'next-auth/react';
import { NextSeo, NextSeoProps } from 'next-seo';
import { useMemo } from 'react';
import { ExpectedProps, ServerPropsFactory } from '@lib/factory/serverprops';
import { collectionMeta } from '@prisma/client';
import { Breadcrumb } from '@components/UIKit/Breadcrumb/Breadcrumb';
import { CollectionManager } from '@data/collection/CollectionManager';
import { CdnPath } from '@specs/cdn';
import { LinksEnum } from '@specs/navigation';
import { useGetSingleCollection } from '@hooks/TRPC_Hooks/useGetSingleCollection';
import { translateType, useTranslation } from '@hooks/useTranslation';
import { PageLayout } from '@components/PageLayout/PageLayout';
import { CollectionArtistsList } from '@components/Pages/CollectionPage/CollectionArtistsList/CollectionArtistsList';
import { CollectionStats } from '@components/Pages/CollectionPage/CollectionStats/CollectionStats';
import { CollectionToysList } from '@components/Pages/CollectionPage/CollectionToyList/CollectionToysList';
import { CollectionToysPublicList } from '@components/Pages/CollectionPage/CollectionToyList/CollectionToysPublicList';
import { FilterToysRaritySelect } from '@components/Pages/ExplorePage/FilterToysRaritySelect/FilterToysRaritySelect';
import { Sorting } from '@components/Pages/FeedPage/Sorting';
import { LogoLoader } from '@components/UIKit/LogoLoader/LogoLoader';
import { Search } from '@components/UIKit/Search/Search';
import { OptimizedImage } from '@components/OptimizedImage/OptimizedImage';
import s from '../collectionsPage.module.scss';
const getSeoConfig = (collection: collectionMeta, tr: translateType): NextSeoProps => {
  const title = tr({
    id: 'collection.page.title'
  }, {
    collectionName: collection.name
  });
  const description = tr({
    id: 'collection.page.description'
  }, {
    collectionName: collection.name
  });
  const image = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/collection-meta/${collection.imageid}.original@3x.webp`;
  return {
    title,
    description,
    openGraph: {
      title,
      description,
      images: [{
        url: image,
        width: 1258,
        height: 1508,
        alt: collection.name
      }]
    },
    twitter: {
      site: '@rtoysapp',
      handle: '@rtoysapp',
      cardType: 'summary_large_image'
    },
    additionalMetaTags: [{
      name: 'twitter:title',
      content: title
    }, {
      name: 'twitter:description',
      content: description
    }, {
      name: 'twitter:image',
      content: image
    }]
  };
};
export const getServerSideProps = ServerPropsFactory(async (props: ExpectedProps) => {
  const [initialCollection] = await Promise.all([props?.slug ? CollectionManager.getSingle(Number(props.slug)) : Promise.resolve(null)]);
  return {
    ...props,
    initialCollection: initialCollection as collectionMeta
  } satisfies PageProps;
}, [{
  key: 'initialCollection',
  destination: LinksEnum.ERROR_404
}]);
export interface PageProps extends ExpectedProps {
  initialCollection: collectionMeta;
}
const Page: NextPage<PageProps> = ({
  initialCollection
}) => {
  const {
    status
  } = useSession();
  const {
    translate: tr
  } = useTranslation();
  //  После получения коллекции из клиентского хука, используем данные из него.
  //  Это помогает корректно инвалидировать кэш и избежать проблем с гидрацией
  const {
    data: clientCollection
  } = useGetSingleCollection({
    id: initialCollection.id
  });
  const collection = clientCollection ?? initialCollection;
  const {
    name,
    description,
    id,
    imageid
  } = collection;
  const seoConfig = useMemo(() => getSeoConfig(collection, tr), [collection, tr]);
  return <PageLayout showBackIconOnMobile data-sentry-element="PageLayout" data-sentry-component="Page" data-sentry-source-file="index.tsx">
            <NextSeo {...seoConfig} data-sentry-element="NextSeo" data-sentry-source-file="index.tsx" />
            <div className={s.collectionPage}>
                <div className={cn(s.collectionPage__container, '_container')}>
                    <Breadcrumb slugTitle={name} data-sentry-element="Breadcrumb" data-sentry-source-file="index.tsx" />

                    <h1 className={cn(s.collectionPage__title, '_title')}>
                        {name}
                    </h1>

                    <div className={s.collectionPage__body}>
                        <div className={s.collectionPage__left}>
                            <Search boxClassName={s.collectionPage__search} placeholder={tr({
              id: 'feed.page.toys.placeholder'
            })} name={'collections'} data-sentry-element="Search" data-sentry-source-file="index.tsx" />
                            <div className={s.collectionPage__sorting}>
                                <FilterToysRaritySelect data-sentry-element="FilterToysRaritySelect" data-sentry-source-file="index.tsx" />
                                <Sorting className={s.collectionPage__sorting__sortItem} data-sentry-element="Sorting" data-sentry-source-file="index.tsx" />
                            </div>

                            {status === 'authenticated' ? <CollectionToysList collection={collection} id={id} /> : status === 'unauthenticated' ? <CollectionToysPublicList collection={collection} id={id} /> : <LogoLoader />}
                        </div>

                        <div className={s.collectionPage__divider} />
                        <div className={s.collectionPage__right}>
                            <OptimizedImage boxClassName={s.collectionPage__image} cdnId={imageid} alt={name} path={CdnPath.collectionMeta} fill style={{
              objectFit: 'cover'
            }} data-sentry-element="OptimizedImage" data-sentry-source-file="index.tsx" />

                            <CollectionStats id={id} data-sentry-element="CollectionStats" data-sentry-source-file="index.tsx" />

                            <p className={s.collectionPage__description}>{parse(description)}</p>

                            <div className={s.collectionPage__divider_horizontal} />

                            <CollectionArtistsList className={s.collectionPage__artists} collectionId={id} data-sentry-element="CollectionArtistsList" data-sentry-source-file="index.tsx" />
                        </div>

                    </div>
                </div>
            </div>
        </PageLayout>;
};
export default Page;